// ============================================
// BLOG VALIDATION UTILITIES
// ============================================
// Type safety ve validation için Zod schemas

import { z } from 'zod';

// ============================================
// ZOD SCHEMAS
// ============================================

export const BlogAuthorSchema = z.object({
  name: z.string().min(1, "Author name is required"),
  avatar: z.string().url("Avatar must be a valid URL").or(z.string().startsWith("/", "Avatar must be a path")),
  title: z.string().min(1, "Author title is required")
});

export const BlogMetadataSchema = z.object({
  id: z.string().min(1, "ID is required"),
  slug: z.string()
    .min(1, "Slug is required")
    .regex(/^[a-z0-9-]+$/, "Slug must be lowercase alphanumeric with hyphens"),
  title: z.string()
    .min(10, "Title must be at least 10 characters")
    .max(100, "Title must be less than 100 characters"),
  seoTitle: z.string().max(60, "SEO title must be less than 60 characters").optional(),
  metaDescription: z.string()
    .max(160, "Meta description must be less than 160 characters")
    .optional(),
  excerpt: z.string()
    .min(50, "Excerpt must be at least 50 characters")
    .max(200, "Excerpt must be less than 200 characters"),
  coverImage: z.string().startsWith("/", "Cover image must be a path"),
  author: BlogAuthorSchema,
  publishedAt: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Date must be in YYYY-MM-DD format"),
  readTime: z.string().regex(/^\d+\s*(dakika|dk)$/, "Read time must be in format '8 dakika'"),
  category: z.string().min(1, "Category is required"),
  tags: z.array(z.string()).min(1, "At least one tag is required").max(10, "Maximum 10 tags allowed")
});

export const BlogContentBlockSchema = z.object({
  type: z.enum(['paragraph', 'heading', 'subheading', 'image', 'list', 'quote', 'table', 'faq']),
  value: z.union([z.string(), z.array(z.string())]).optional(),
  alt: z.string().optional(),
  id: z.string().optional(),
  items: z.array(z.string()).optional(),
  headers: z.array(z.string()).optional(),
  rows: z.array(z.array(z.string())).optional(),
  caption: z.string().optional(),
  faqs: z.array(z.object({
    question: z.string(),
    answer: z.string()
  })).optional(),
  faqTitle: z.string().optional(),
  faqDescription: z.string().optional()
});

export const BlogContentSchema = z.object({
  tableOfContents: z.array(z.object({
    id: z.string(),
    title: z.string()
  })).optional(),
  content: z.array(BlogContentBlockSchema).min(1, "Content must have at least one block")
});

export const BlogPostSchema = BlogMetadataSchema.merge(BlogContentSchema);

// ============================================
// VALIDATION FUNCTIONS
// ============================================

export function validateBlogMetadata(data: unknown) {
  try {
    return {
      success: true,
      data: BlogMetadataSchema.parse(data)
    };
  } catch (error) {
    if (error instanceof z.ZodError) {
      return {
        success: false,
        errors: error.errors.map(err => ({
          path: err.path.join('.'),
          message: err.message
        }))
      };
    }
    return {
      success: false,
      errors: [{ path: 'unknown', message: 'Validation failed' }]
    };
  }
}

export function validateBlogContent(data: unknown) {
  try {
    return {
      success: true,
      data: BlogContentSchema.parse(data)
    };
  } catch (error) {
    if (error instanceof z.ZodError) {
      return {
        success: false,
        errors: error.errors.map(err => ({
          path: err.path.join('.'),
          message: err.message
        }))
      };
    }
    return {
      success: false,
      errors: [{ path: 'unknown', message: 'Validation failed' }]
    };
  }
}

export function validateBlogPost(data: unknown) {
  try {
    return {
      success: true,
      data: BlogPostSchema.parse(data)
    };
  } catch (error) {
    if (error instanceof z.ZodError) {
      return {
        success: false,
        errors: error.errors.map(err => ({
          path: err.path.join('.'),
          message: err.message
        }))
      };
    }
    return {
      success: false,
      errors: [{ path: 'unknown', message: 'Validation failed' }]
    };
  }
}

// ============================================
// VALIDATION HELPERS
// ============================================

/**
 * Slug'ın geçerli olup olmadığını kontrol eder
 */
export function isValidSlug(slug: string): boolean {
  return /^[a-z0-9-]+$/.test(slug);
}

/**
 * Image path'in geçerli olup olmadığını kontrol eder
 */
export function isValidImagePath(path: string): boolean {
  return path.startsWith('/') && /\.(jpg|jpeg|png|webp|gif)$/.test(path.toLowerCase());
}

/**
 * Tarih formatının geçerli olup olmadığını kontrol eder
 */
export function isValidDateFormat(date: string): boolean {
  return /^\d{4}-\d{2}-\d{2}$/.test(date);
}

/**
 * Content block'un geçerli olup olmadığını kontrol eder
 */
export function validateContentBlock(block: unknown) {
  return validateBlogContent({ content: [block] });
}

// ============================================
// BULK VALIDATION
// ============================================

/**
 * Tüm blog metadata'larını validate eder
 */
export function validateAllBlogMetadata(metadataArray: unknown[]) {
  const results = metadataArray.map((metadata, index) => ({
    index,
    slug: (metadata as any)?.slug || 'unknown',
    ...validateBlogMetadata(metadata)
  }));
  
  const failed = results.filter(r => !r.success);
  
  return {
    total: results.length,
    passed: results.length - failed.length,
    failed: failed.length,
    errors: failed
  };
}

/**
 * SEO uyarılarını kontrol eder
 */
export function checkSEOWarnings(metadata: any) {
  const warnings: string[] = [];
  
  if (!metadata.seoTitle) {
    warnings.push('SEO title is missing (will use regular title)');
  }
  
  if (!metadata.metaDescription) {
    warnings.push('Meta description is missing (will use excerpt)');
  }
  
  if (metadata.title.length > 60) {
    warnings.push('Title is longer than 60 characters (may be truncated in search results)');
  }
  
  if (metadata.excerpt.length < 100) {
    warnings.push('Excerpt is shorter than 100 characters (consider expanding)');
  }
  
  if (metadata.tags.length < 3) {
    warnings.push('Less than 3 tags (consider adding more for better categorization)');
  }
  
  return warnings;
}

// ÖRNEK KULLANIM:
// const validation = validateBlogMetadata({
//   id: "test-blog",
//   slug: "test-blog",
//   title: "Test Blog Post",
//   // ... other fields
// });
// if (!validation.success) {
//   console.error("Validation errors:", validation.errors);
// }
